<template>
  <div
    class="inline-flex items-center meridia-radio-button-wrapper"
    :class="{
      disabled: disabled,
      'cursor-not-allowed': disabled,
      'cursor-pointer': !disabled,
    }"
  >
    <PrimeRadioButton
      v-model="modelValue"
      :name="name"
      :value="value"
      :input-id="computedId"
      unstyled
      :pt="passThrough"
      :disabled="disabled"
      class="meridia-radio-button"
      :class="sizeClass"
    />
    <label
      :for="computedId"
      class="radio-button-label"
      :class="getPtClass < PassThroughMeridiaCheckbox > ('label', passThrough)"
    >
      <slot v-if="$slots.label" name="label" :label="label" />
      <div v-else>{{ label }}</div>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PassThroughMeridiaCheckbox, RadioButtonSize } from './types';
import { usePassThrough } from 'primevue/passthrough';
import _ from 'lodash';
import { getPtClass } from '../utils';
import PrimeRadioButton from 'primevue/radiobutton';

const modelValue = defineModel<any>({ required: true });

const props = withDefaults(
  defineProps<{
    name: string;
    value: any;
    size?: `${RadioButtonSize}`;
    label?: string;
    disabled?: boolean;
    pt?: PassThroughMeridiaCheckbox;
  }>(),
  {
    size: RadioButtonSize.BIG,
    disabled: false,
  }
);

defineEmits<{
  (event: 'update:model-value', value: boolean): void;
}>();

defineSlots<{
  label: (label?: string) => any;
}>();

const computedId = computed(() => _.uniqueId('checkbox-'));

const sizeClass = computed(() => ({
  'size-small': props.size === RadioButtonSize.SMALL,
  'size-big': props.size === RadioButtonSize.BIG,
}));

const passThrough = usePassThrough(
  props.pt,
  {
    input: () => ({
      class: {
        'radio-button-input': true,
      },
    }),
    box: (options) => ({
      class: {
        'radio-button-box': true,
        checked: options.context.checked,
        disabled: options.context.disabled,
      },
    }),
    icon: () => ({
      class: {
        'radio-button-icon': true,
      },
    }),
  } as PassThroughMeridiaCheckbox,
  { mergeSections: false, mergeProps: true }
) as PassThroughMeridiaCheckbox;

defineExpose({
  computedId,
});
</script>

<style lang="scss">
.meridia-radio-button-wrapper {
  .radio-button-label {
    @apply text-neutral-text-body text-body-base cursor-pointer pl-xs inline-flex items-center transition-all duration-200;
  }

  .meridia-radio-button {
    @apply relative inline-flex;

    .radio-button-input {
      @apply appearance-none cursor-pointer absolute top-0 w-full h-full p-0 m-0 opacity-0 z-[1] outline-none border-0 radius-full;
    }
    .radio-button-icon {
      @apply bg-white rounded-full;
      transform: translateZ(0) scale(1, 1);
    }
    &.size-small {
      @apply w-md h-md;
      .radio-button-icon {
        @apply w-xs h-xs;
      }
    }
    &.size-big {
      @apply w-xl h-xl;
      .radio-button-icon {
        @apply w-s h-s;
      }
    }
    .radio-button-box {
      @apply w-[inherit] h-[inherit] flex justify-center items-center radius-full transition-all duration-200 border-2;
    }
    .radio-button-box:not(.checked) {
      @apply border-neutral-border-darker;
    }
    .radio-button-box.checked {
      @apply border-primary-border-default bg-primary-surface-default;
    }
  }

  &.disabled {
    @apply opacity-40;
    .radio-button-input {
      @apply cursor-not-allowed;
    }

    .radio-button-label {
      @apply text-neutral-text-disabled cursor-not-allowed;
    }
  }

  &:not(.disabled):hover,
  &:not(.disabled).hover {
    .radio-button-box {
      @apply border-primary-border-default;
    }
    .radio-button-box.checked {
      @apply border-primary-border-darker bg-primary-surface-darker;
    }
  }

  &:not(.disabled):focus {
    .radio-button-box {
      box-shadow: 0px 0px 0px 2px rgba(167, 241, 206, 0.4);
    }
  }
}
</style>
